import React, { forwardRef } from "react"

import { useField, useFormikContext } from "formik"

import {
  ClipboardDocumentListIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid"

import classNames from "src/classNames"

export type FileInputProps = {
  label: string
  name: string
  description?: string
  photo?: boolean
  hideErrors?: boolean
}

const FileInput = forwardRef<
  HTMLInputElement,
  FileInputProps & React.HTMLProps<HTMLInputElement>
>(
  (
    {
      name,
      label,
      description = "",
      hideErrors = false,
      photo = false,
      ...props
    },
    ref: any
  ) => {
    const [field, meta] = useField({ name })
    const { setFieldValue } = useFormikContext()

    const error = meta.error ? meta.error : ""

    return (
      <div className={classNames(!error && "mb-5")}>
        <label
          htmlFor={name}
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {label}
        </label>
        <div className="mt-1 flex items-center gap-x-3">
          {field.value?.url ? (
            <img
              className={classNames(
                "h-12 w-12",
                photo && "rounded-full object-cover"
              )}
              src={field.value.url}
              alt="preview"
            />
          ) : photo ? (
            <UserCircleIcon
              className="h-12 w-12 text-gray-300"
              aria-hidden="true"
            />
          ) : (
            <ClipboardDocumentListIcon
              className="h-12 w-12 text-gray-300"
              aria-hidden="true"
            />
          )}
          <label
            htmlFor={`${field.name}-file-input`}
            className="cursor-pointer rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {field.value?.url ? "Change File" : "Select File"}
          </label>
          <input
            type="file"
            id={`${field.name}-file-input`}
            className="hidden"
            onChange={e => setFieldValue(name, e.currentTarget.files[0])}
            ref={ref}
            {...props}
          />
          <span>{field.value?.name}</span>
        </div>
        <div className="text-sm">
          {!hideErrors && error ? (
            <p className="text-red-600">{error}</p>
          ) : (
            <p className="text-gray-500">{description}</p>
          )}
        </div>
      </div>
    )
  }
)

export default FileInput
